import { computed, inject, Injectable, Injector, signal } from '@angular/core';
import { handleRequestError } from '@softline/application';
import { CreditRepository, DocumentCreditLimit } from '@softapps/core/customer-info';
import { cloneDocumentRequest } from '../helper/clone-document-request';
import {
  FinalizeSalesDocumentResult,
  FinalizeStep,
  SalesDocumentRepository,
} from '../repositories/sales-document.repository';
import { CloneDocumentRequest } from '../types/clone-document-request';
import { CreateOrUpdateSalesDocumentRequest } from '../types/create-or-update-sales-document-request';
import { DocumentFormData, EMPTY_DOCUMENT_FORM_DATA } from '../types/document-form-data';
import { DocumentType } from '../types/document-type';
import { SalesDocument } from '../types/sales-document';
import { SalesDocumentValidation } from '../types/sales-document-validation';

/** Diagnostics for the documents/clone itemIds tri-state (ClickUp 869eyh3xj):
    whether the key goes out at all is the whole contract, and the answer says
    which Belegart the backend picked. `console.debug` stays out of the default
    console (verbose level only).
    TODO: drop once the tri-state is confirmed against the deployed backend. */
function debugClone(subject: string, detail: unknown): void {
  console.debug(`sales:clone ${subject}`, JSON.stringify(detail));
}

@Injectable()
export class SalesDocumentService {
  private readonly repository = inject(SalesDocumentRepository);
  private readonly creditRepository = inject(CreditRepository);
  private readonly injector = inject(Injector);

  private normalizeDocumentData(
    value: Partial<DocumentFormData> | null | undefined
  ): DocumentFormData {
    if (!value) {
      return { ...EMPTY_DOCUMENT_FORM_DATA };
    }

    const normalized = { ...EMPTY_DOCUMENT_FORM_DATA };

    for (const key of Object.keys(EMPTY_DOCUMENT_FORM_DATA) as Array<
      keyof DocumentFormData
    >) {
      const fieldValue = value[key];
      if (fieldValue !== undefined) {
        (normalized[key] as DocumentFormData[typeof key]) =
          fieldValue as DocumentFormData[typeof key];
      }
    }

    return normalized;
  }

  readonly document = signal<SalesDocument | null>(null);
  readonly documentData = signal<DocumentFormData>(this.normalizeDocumentData(null));
  readonly documentTypes = signal<DocumentType[]>([]);
  readonly isLoading = signal(false);
  readonly error = signal<unknown>(null);
  private readonly createdIds = new Set<string>();

  /** Backend verdict: a finalized/locked document rejects every write. */
  readonly isReadOnly = computed(() => this.document()?.isReadOnly ?? false);

  setDocumentData(value: Partial<DocumentFormData> | null | undefined): void {
    this.documentData.set(this.normalizeDocumentData(value));
  }

  patchDocumentData(value: Partial<DocumentFormData> | null | undefined): void {
    if (!value) {
      return;
    }
    // Every header edit funnels through here; loading uses setDocumentData.
    if (this.isReadOnly()) {
      return;
    }

    this.documentData.update((current) =>
      this.normalizeDocumentData({
        ...current,
        ...value,
      })
    );
  }

  resetDocumentData(): void {
    this.setDocumentData(null);
  }

  /** Clones a document into a new draft. `itemIds` is the backend's tri-state:
   *  omitted clones every position, an empty list copies the Kopfdaten alone,
   *  a list takes exactly those positions with their subtree. `copy` leaves the
   *  source untouched; `fullfill` debits the full captured quantities from it.
   *  Leaves this service's document state alone: the caller navigates into the
   *  new draft, which loads it the normal way. */
  async cloneDocument(
    sourceId: string,
    itemIds?: string[] | null,
    action: CloneDocumentRequest['action'] = 'copy'
  ): Promise<SalesDocument | null> {
    const body = cloneDocumentRequest(sourceId, itemIds, action);
    debugClone('request', {
      body,
      itemIdsKeySent: 'itemIds' in body,
      expected:
        'itemIds' in body
          ? body.itemIds?.length === 0
            ? 'Kopfdaten only'
            : `${body.itemIds?.length} position(s)`
          : 'all positions',
    });
    try {
      const draft = await this.repository.clone(body);
      debugClone('response', {
        id: draft.id,
        documentType: draft.documentType?.type,
        destinationDocumentType: draft.destinationDocumentType?.type ?? null,
        customer: draft.customer?.number ?? null,
      });
      this.createdIds.add(draft.id);
      return draft;
    } catch (e) {
      debugClone('failed', { body, error: String(e) });
      handleRequestError(e, this.injector, { show: true });
      return null;
    }
  }

  async create(body: CreateOrUpdateSalesDocumentRequest): Promise<SalesDocument | null> {
    this.isLoading.set(true);
    this.error.set(null);
    try {
      const result = await this.repository.create(body);
      this.document.set(result);
      this.createdIds.add(result.id);
      return result;
    } catch (e) {
      this.error.set(e);
      handleRequestError(e, this.injector, { show: true });
      return null;
    } finally {
      this.isLoading.set(false);
    }
  }

  /** True once for a document this service created (wizard, clone, template):
      the first visit of a fresh draft asks on leave even without further edits. */
  takeCreated(id: string): boolean {
    return this.createdIds.delete(id);
  }

  async load(id: string): Promise<SalesDocument | null> {
    this.isLoading.set(true);
    this.error.set(null);
    try {
      const result = await this.repository.load(id);
      this.document.set(result);
      return result;
    } catch (e) {
      this.error.set(e);
      handleRequestError(e, this.injector, { show: true });
      return null;
    } finally {
      this.isLoading.set(false);
    }
  }

  async update(
    id: string,
    body: Partial<CreateOrUpdateSalesDocumentRequest>
  ): Promise<SalesDocument | null> {
    this.isLoading.set(true);
    this.error.set(null);
    try {
      const result = await this.repository.update(id, body);
      this.document.set(result);
      return result;
    } catch (e) {
      this.error.set(e);
      handleRequestError(e, this.injector, { show: true });
      return null;
    } finally {
      this.isLoading.set(false);
    }
  }

  /** Creates the Anzahlungsgutschrift for an Auftrag (Ticket 452.648). Returns the
      created document, or null when the backend refused. */
  async createDownPaymentCreditNote(id: string): Promise<SalesDocument | null> {
    this.isLoading.set(true);
    this.error.set(null);
    try {
      return await this.repository.createDownPaymentCreditNote(id);
    } catch (e) {
      this.error.set(e);
      handleRequestError(e, this.injector, { show: true });
      return null;
    } finally {
      this.isLoading.set(false);
    }
  }

  async validate(id: string): Promise<SalesDocumentValidation | null> {
    try {
      return await this.repository.validate(id);
    } catch (e) {
      handleRequestError(e, this.injector, { show: true });
      return null;
    }
  }

  async delete(id: string): Promise<boolean> {
    this.isLoading.set(true);
    this.error.set(null);
    try {
      await this.repository.delete(id);
      this.document.set(null);
      return true;
    } catch (e) {
      this.error.set(e);
      handleRequestError(e, this.injector, { show: true });
      return false;
    } finally {
      this.isLoading.set(false);
    }
  }

  async postpone(id: string): Promise<SalesDocument | null> {
    this.isLoading.set(true);
    this.error.set(null);
    try {
      const result = await this.repository.postpone(id);
      this.document.set(result);
      return result;
    } catch (e) {
      this.error.set(e);
      handleRequestError(e, this.injector, { show: true });
      return null;
    } finally {
      this.isLoading.set(false);
    }
  }

  async finalize(id: string): Promise<FinalizeSalesDocumentResult | null> {
    this.isLoading.set(true);
    this.error.set(null);
    try {
      return await this.repository.finalize(id);
    } catch (e) {
      this.error.set(e);
      handleRequestError(e, this.injector, { show: true });
      return null;
    } finally {
      this.isLoading.set(false);
    }
  }

  async sign(id: string, signature: Blob, signerName?: string | null): Promise<boolean> {
    this.isLoading.set(true);
    this.error.set(null);
    try {
      await this.repository.signDocument(id, signature, signerName);
      return true;
    } catch (e) {
      this.error.set(e);
      handleRequestError(e, this.injector, { show: true });
      return false;
    } finally {
      this.isLoading.set(false);
    }
  }

  async loadSignature(id: string): Promise<Blob | null> {
    try {
      return await this.repository.loadSignature(id);
    } catch (e) {
      handleRequestError(e, this.injector, { show: true });
      return null;
    }
  }

  private documentTypesToken = 0;

  async loadDocumentTypes(customerId?: string): Promise<void> {
    const token = ++this.documentTypesToken;
    try {
      const result = await this.repository.getDocumentTypes(customerId);
      if (token === this.documentTypesToken) this.documentTypes.set(result);
    } catch (e) {
      handleRequestError(e, this.injector, { show: true });
    }
  }

  async loadDocumentTypesForDocument(id: string): Promise<void> {
    const token = ++this.documentTypesToken;
    try {
      const result = await this.repository.getDocumentTypesForDocument(id);
      if (token === this.documentTypesToken) this.documentTypes.set(result);
    } catch (e) {
      handleRequestError(e, this.injector, { show: true });
    }
  }

  async getFinalizeSteps(id: string): Promise<FinalizeStep[] | null> {
    try {
      return await this.repository.getFinalizeSteps(id);
    } catch (e) {
      handleRequestError(e, this.injector, { show: true });
      return null;
    }
  }

  async getCreditLimit(id: string): Promise<DocumentCreditLimit | null> {
    try {
      return await this.creditRepository.getDocumentCreditLimit(id);
    } catch (e) {
      handleRequestError(e, this.injector, { show: true });
      return null;
    }
  }
}
